import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/design-system/primitives';

type Persona = 'agency' | 'manager' | 'employee';

const PERSONA_LABELS: Record<Persona, { title: string; subtitle: string; tileBg: string; route: string }> = {
  agency: { title: 'Back-office agence', subtitle: 'Pilotage agence', tileBg: 'oklch(0.62 0.17 30)', route: '/dashboard' },
  manager: { title: 'Manager', subtitle: 'Chef d\u2019équipe client', tileBg: 'oklch(0.65 0.16 80)', route: '/team' },
  employee: { title: 'Intérimaire', subtitle: 'Self-service', tileBg: 'oklch(0.58 0.15 150)', route: '/my/mission' },
};

function personaFromPath(pathname: string): Persona {
  if (pathname.startsWith('/team')) return 'manager';
  if (pathname.startsWith('/my')) return 'employee';
  return 'agency';
}

export const PersonaSwitcher: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const current = useMemo(() => personaFromPath(location.pathname), [location.pathname]);
  const cfg = PERSONA_LABELS[current];

  const handlePick = (p: Persona) => {
    setOpen(false);
    if (p !== current) navigate(PERSONA_LABELS[p].route);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 h-10 pl-1 pr-3 rounded border border-border bg-surface hover:bg-surface-muted text-sm"
      >
        <span
          className="w-8 h-8 rounded flex items-center justify-center text-white"
          style={{ background: cfg.tileBg }}
          aria-hidden
        >
          <span className="w-3 h-3 bg-white/90 rounded-sm" />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] text-text-mute uppercase tracking-wide">Persona</span>
          <span className="text-sm font-semibold text-text">{cfg.title}</span>
        </span>
        <Icon name="ChevronDown" size={14} className="text-text-mute" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-surface border border-border rounded shadow-pop z-50">
          {(Object.keys(PERSONA_LABELS) as Persona[]).map((p) => {
            const meta = PERSONA_LABELS[p];
            const isCurrent = p === current;
            return (
              <button
                key={p}
                type="button"
                onClick={() => handlePick(p)}
                className={`w-full text-left px-3 py-2 hover:bg-surface-muted flex flex-col gap-0.5 ${isCurrent ? 'bg-accent-soft/30' : ''}`}
              >
                <span className="text-sm text-text font-medium flex items-center gap-1">
                  {meta.title}
                  {isCurrent && <Icon name="Check" size={14} className="text-accent" />}
                </span>
                <span className="text-xs text-text-mute">{meta.subtitle}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
